import React, { useState } from 'react';
import { 
  ShieldCheck, Link as LinkIcon, Edit3, Plus, 
  Trash2, ExternalLink, Save, ArrowLeft, 
  FileText, Globe 
} from 'lucide-react';

// ✅ CONEXIÓN AL SISTEMA NERVIOSO CENTRAL (Ruta Absoluta)
import { useGlobal } from 'context/GlobalContext';

const GestionLegal = () => {
  // 1. Conexión global para persistencia de documentos legales en la Nube
  const { enlacesLegales, setEnlacesLegales } = useGlobal();

  const [vista, setVista] = useState('lista'); 
  const [enlaceEditando, setEnlaceEditando] = useState(null);
  const [formData, setFormData] = useState({ titulo: '', url: '', desc: '' });

  const abrirFormulario = (enlace = null) => {
    if (enlace) {
      setEnlaceEditando(enlace);
      setFormData({ titulo: enlace.tituloHipervinculo, url: enlace.urlCompleta, desc: enlace.descripcion });
    } else {
      setEnlaceEditando(null);
      setFormData({ titulo: '', url: '', desc: '' });
    }
    setVista('editar');
  };

  const guardarCambios = (e) => {
    e.preventDefault();
    const nuevoEnlace = {
      id: enlaceEditando ? enlaceEditando.id : Date.now(),
      tituloHipervinculo: formData.titulo.toUpperCase(),
      urlCompleta: formData.url,
      descripcion: formData.desc
    };

    if (enlaceEditando) {
      setEnlacesLegales(enlacesLegales.map(en => en.id === enlaceEditando.id ? nuevoEnlace : en));
    } else {
      setEnlacesLegales([...enlacesLegales, nuevoEnlace]);
    }
    setVista('lista');
    alert("✅ Repositorio legal actualizado y sincronizado.");
  };

  const eliminarEnlace = (id) => {
    if(window.confirm("¿ELIMINAR DOCUMENTO? Esta acción es irreversible y los padres ya no podrán visualizarlo en su App.")) {
      setEnlacesLegales(enlacesLegales.filter(e => e.id !== id));
    }
  };

  return (
    <div className="w-full max-w-[1200px] mx-auto space-y-8 animate-in fade-in duration-500 pb-24 px-4 text-left font-sans">
      
      {vista === 'lista' ? (
        <>
          {/* HEADER SECCIÓN LEGAL */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b-4 border-slate-100 pb-8">
            <div className="space-y-2 text-left">
              <div className="inline-flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-full shadow-lg">
                <ShieldCheck size={18} className="text-emerald-400" />
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-100 italic">Marco Normativo IgleKids</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-black text-slate-900 italic tracking-tighter uppercase leading-none">Documentos</h2>
            </div>

            <button 
              onClick={() => abrirFormulario()}
              className="bg-[#0A2440] text-white px-8 py-5 rounded-[2.5rem] flex items-center justify-center gap-3 shadow-xl hover:bg-indigo-600 transition-all active:scale-95 border-b-8 border-slate-800"
            >
              <Plus size={24} className="text-emerald-400" />
              <span className="font-black text-xs uppercase tracking-widest">Nuevo Recurso</span>
            </button>
          </div>

          {/* GRID DE ENLACES */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {enlacesLegales.length === 0 ? (
              <div className="col-span-full py-24 text-center bg-slate-50 rounded-[4rem] border-4 border-dashed border-slate-200 opacity-40">
                 <FileText size={60} className="mx-auto mb-4 text-slate-400" />
                 <p className="font-black uppercase tracking-widest text-slate-500 italic">No hay documentos en el repositorio</p>
              </div>
            ) : (
              enlacesLegales.map(enlace => (
                <div key={enlace.id} className="bg-white p-8 rounded-[3.5rem] shadow-sm border-2 border-slate-50 space-y-6 hover:shadow-2xl hover:border-indigo-100 transition-all group relative overflow-hidden">
                  
                  <div className="flex justify-between items-start relative z-10">
                    <div className="w-14 h-14 rounded-2xl bg-indigo-50 text-indigo-500 flex items-center justify-center shadow-inner group-hover:bg-indigo-600 group-hover:text-white transition-all">
                      <FileText size={28} />
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => abrirFormulario(enlace)} className="p-3 bg-slate-50 rounded-xl text-slate-300 hover:text-indigo-600 transition-colors shadow-sm"><Edit3 size={18} /></button>
                      <button onClick={() => eliminarEnlace(enlace.id)} className="p-3 bg-slate-50 rounded-xl text-slate-300 hover:text-rose-500 transition-colors shadow-sm"><Trash2 size={18} /></button>
                    </div>
                  </div>

                  <div className="space-y-1 text-left relative z-10">
                    <h3 className="text-xl font-black text-slate-800 uppercase italic tracking-tighter leading-tight">{enlace.tituloHipervinculo}</h3>
                    <div className="flex items-center gap-2 text-slate-400">
                      <Globe size={12} />
                      <span className="text-[9px] font-bold truncate max-w-[200px] uppercase tracking-widest italic">{enlace.urlCompleta}</span>
                    </div>
                  </div>

                  <p className="text-xs text-slate-500 font-bold leading-relaxed italic border-t border-slate-50 pt-4 text-left">
                    "{enlace.descripcion}"
                  </p>

                  <a 
                    href={enlace.urlCompleta} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-full py-4 bg-slate-50 text-indigo-600 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] flex items-center justify-center gap-2 hover:bg-indigo-50 transition-all border border-indigo-100 active:scale-95"
                  >
                    Abrir Documento <ExternalLink size={14} />
                  </a>
                </div>
              ))
            )}
          </div>
        </>
      ) : (
        /* EDITOR DE DOCUMENTO */
        <div className="max-w-3xl mx-auto space-y-6 animate-in slide-in-from-right-8 duration-500">
          <button onClick={() => setVista('lista')} className="flex items-center gap-2 text-slate-400 font-black text-[10px] uppercase tracking-widest hover:text-indigo-600 transition-colors ml-4 italic">
            <ArrowLeft size={16} /> Volver al Repositorio
          </button>

          <form onSubmit={guardarCambios} className="bg-white rounded-[4.5rem] shadow-2xl border-4 border-slate-50 overflow-hidden">
            <div className="bg-[#0A2440] p-10 md:p-14 text-white text-center relative overflow-hidden">
               <div className="absolute top-0 right-0 w-40 h-40 bg-indigo-500/10 rounded-full -mr-20 -mt-20 blur-3xl" />
               <span className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.4em] italic mb-3 block">Legal & Privacy Center</span>
               <h3 className="text-4xl font-black italic tracking-tighter uppercase leading-none">Editor de Recurso</h3>
            </div>
            
            <div className="p-10 md:p-16 space-y-10">
              <div className="space-y-8 text-left">
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-6 italic">Título del Documento</label>
                  <input 
                    type="text" required placeholder="EJ. REGLAMENTO INTERNO 2026"
                    value={formData.titulo} onChange={e => setFormData({...formData, titulo: e.target.value.toUpperCase()})}
                    className="w-full p-6 bg-slate-50 rounded-[2.2rem] border-2 border-transparent focus:border-indigo-500 font-black text-xl outline-none shadow-inner uppercase"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-6 italic">Enlace Público (PDF / Web)</label>
                  <div className="relative">
                    <LinkIcon size={20} className="absolute left-6 top-1/2 -translate-y-1/2 text-indigo-400" />
                    <input 
                      type="url" required placeholder="https://biblioteca.iglekids.org/documento.pdf"
                      value={formData.url} onChange={e => setFormData({...formData, url: e.target.value})}
                      className="w-full pl-16 pr-6 py-6 bg-slate-50 rounded-[2.2rem] border-2 border-transparent focus:border-indigo-500 font-bold text-slate-700 outline-none shadow-inner"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-6 italic">Resumen Informativo</label>
                  <textarea 
                    placeholder="Describe brevemente el propósito de este documento para los padres..."
                    value={formData.desc} onChange={e => setFormData({...formData, desc: e.target.value})}
                    className="w-full p-8 bg-slate-50 rounded-[2.5rem] border-none text-sm font-bold text-slate-700 h-32 resize-none outline-none focus:ring-4 focus:ring-indigo-500/5 shadow-inner italic"
                  />
                </div>
              </div>

              <div className="bg-amber-50 p-6 rounded-[2.5rem] border-2 border-amber-100 flex items-start gap-4 text-left">
                <ExternalLink size={24} className="text-amber-500 shrink-0 mt-1" />
                <p className="text-[10px] font-bold text-amber-900 leading-relaxed uppercase tracking-widest italic">
                  Nota de seguridad: Los padres podrán visualizar y descargar este recurso directamente desde sus dispositivos móviles. Asegúrate de que el enlace sea permanente.
                </p>
              </div>

              <button 
                type="submit"
                className="w-full bg-slate-900 text-white py-8 rounded-[3.5rem] font-black text-sm uppercase tracking-[0.3em] shadow-2xl hover:bg-indigo-600 active:scale-95 transition-all flex items-center justify-center gap-4 border-b-8 border-slate-700"
              >
                <Save size={24} className="text-emerald-400" /> Guardar y Sincronizar
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default GestionLegal;